import { useEffect, useRef, useState } from 'react';
import { get } from './api';
import type { Person } from './types';

export interface Picked { email: string; name: string; external: boolean; oid?: string; jobTitle?: string | null; unit?: string | null }

/**
 * Busca de pessoas no Entra ID (Microsoft Graph). Com `allowExternal`, aceita também um e-mail
 * digitado (responsáveis e convidados de fora do colégio). Com `allowText`, aceita também um nome digitado que
 * não está no diretório (ex.: um terceirizado que retira uma chave).
 */
export function PeoplePicker({ id, placeholder, onPick, allowExternal = false, allowText = false, exclude = [], buttonLabel = 'Adicionar', autoFocus = false }: {
  id: string; placeholder: string; onPick: (p: Picked) => void; allowExternal?: boolean; allowText?: boolean; exclude?: string[]; buttonLabel?: string; autoFocus?: boolean;
}) {
  const [q, setQ] = useState('');
  const [list, setList] = useState<Person[]>([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || term.includes('@')) { setList([]); return; }
    const my = ++seq.current;
    const t = window.setTimeout(() => {
      get<Person[]>('people?q=' + encodeURIComponent(term))
        .then(r => { if (my === seq.current) { setList(r.filter(p => !exclude.includes(p.upn.toLowerCase()))); setOpen(true); } })
        .catch(() => undefined);
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (p: Picked) => { onPick(p); setQ(''); setList([]); setOpen(false); };
  const submit = () => {
    const v = q.trim();
    if (allowExternal && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return pick({ email: v.toLowerCase(), name: v, external: true });
    // Nome digitado: usa a pessoa do diretório com esse nome exato; senão, o próprio texto
    const same = list.find(p => p.name.toLowerCase() === v.toLowerCase());
    if (allowText && same) return pick({ email: same.upn.toLowerCase(), name: same.name, external: false, oid: same.oid, jobTitle: same.jobTitle, unit: same.unit ?? null });
    if (allowText && v.length >= 2) return pick({ email: '', name: v, external: true });
    if (list[0]) return pick({ email: list[0].upn.toLowerCase(), name: list[0].name, external: false, oid: list[0].oid, jobTitle: list[0].jobTitle, unit: list[0].unit ?? null });
  };

  return (
    <div className="people-add">
      <div className="suggestions">
        <input type="text" id={id} value={q} placeholder={placeholder} autoComplete="off" autoFocus={autoFocus} aria-autocomplete="list" aria-expanded={open && list.length > 0}
          onChange={e => setQ(e.target.value)} onFocus={() => list.length && setOpen(true)} onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} />
        {open && list.length > 0 && (
          <ul className="ac-list" role="listbox">
            {list.map(p => (
              <li key={p.oid}><button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pick({ email: p.upn.toLowerCase(), name: p.name, external: false, oid: p.oid, jobTitle: p.jobTitle, unit: p.unit ?? null })}>
                {p.name}<small>{p.jobTitle ?? p.upn}</small></button></li>
            ))}
          </ul>
        )}
      </div>
      <button type="button" className="btn ghost sm" onClick={submit}>{buttonLabel}</button>
    </div>
  );
}
